import { useMsal } from '@azure/msal-react';
import { clearUserSession } from '../utils/authUtils';
import LogoutIcon from '@mui/icons-material/Logout';
import React, { useState } from 'react';
import { useAccount } from '../App';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Tabs,
  Tab,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createUseStyles } from 'react-jss';
import HelpModal from './HelpModal/HelpModal';
import {
  colors,
  cssColorSecondary,
  cssSpacingSm,
} from '../constants/globalcss';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

const useStyles = createUseStyles({
  appBar: {
    backgroundColor: colors.primary,
    boxShadow: 'none',
    color: colors.text.light,
    zIndex: 1201,
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    width: '100%',
  },
  homeButton: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
    borderRadius: 0,
    color: colors.text.light,
    marginRight: cssSpacingSm,
    padding: cssSpacingSm,
  },
  logo: {
    // filter: 'brightness(0) invert(1)',
    height: 56,
    maxWidth: 200,
    objectFit: 'contain',
    width: 'auto',
  },
  menuButton: {
    '&:hover': {
      backgroundColor: cssColorSecondary,
    },
    color: colors.text.light,
    marginLeft: cssSpacingSm,
  },
  tabs: {
    '& .Mui-selected': {
      background: colors.text.light,
      borderBottom: 'none',
      borderRadius: '8px',
      boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
      color: `${colors.primary} !important`,
    },
    '& .MuiTab-root': {
      borderBottom: 'none',
      borderRadius: '8px',
      color: colors.text.light,
      fontWeight: 600,
      minWidth: 80,
      textTransform: 'none',
      transition: 'background 0.2s',
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    borderBottom: 'none',
  },
  tabsWrapper: {
    background: 'rgba(255,255,255,0.10)',
    borderRadius: '8px',
    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
    display: 'inline-block',
    minHeight: 44,
    padding: 0,
  },
  title: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
  },
});

export default function PortalAppBar() {
  const classes = useStyles();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<string>(() => {
    const path = window.location.pathname;
    if (path.includes('/workflows')) return 'workflows';
    return 'requests';
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    switch (newValue) {
      case 'requests':
        navigate('/');
        break;
      case 'workflows':
        navigate('/workflows');
        break;
      default:
    }
  };

  return (
    <AppBar position="fixed" elevation={0} className={classes.appBar}>
      <Toolbar>
        <Box className={classes.container}>
          {/* Left section: Logo, Title, Tabs */}
          <Box sx={{ alignItems: 'center', display: 'flex', flex: 1 }}>
            <img src="/PRP1.svg" alt="Company Logo" className={classes.logo} />
            <Typography
              variant="h4"
              noWrap
              component="div"
              className={classes.title}
              sx={{ flexGrow: 0, fontWeight: 500, ml: 1, mr: 2 }}
            >
              Servicer Portal
            </Typography>
            <div className={classes.tabsWrapper}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                className={classes.tabs}
              >
                <Tab
                  label={
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                      <InsertDriveFileIcon fontSize="small" />
                      Requests
                    </Box>
                  }
                  value="requests"
                />
                <Tab
                  label={
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                      <NotificationsActiveIcon fontSize="small" />
                      Workflows
                    </Box>
                  }
                  value="workflows"
                />
              </Tabs>
            </div>
          </Box>

          {/* Right section: Avatar and Username dropdown */}
          <UserMenu classes={classes} />
        </Box>
      </Toolbar>
    </AppBar>
  );
}

function UserMenu({ classes }: { classes: Record<string, string> }) {
  // Replace with actual user info if available
  const { activeAccount } = useAccount();
  const username = activeAccount ? activeAccount.name : 'User';
  const { instance } = useMsal();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = React.useState(false);
  const [helpModalOpen, setHelpModalOpen] = React.useState(false);
  const open = Boolean(anchorEl);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleSignOut = async () => {
    setLoading(true);
    handleClose();
    try {
      await clearUserSession(instance);
    } finally {
      setLoading(false);
    }
  };
  const handleGetHelp = () => {
    handleClose();
    setHelpModalOpen(true);
  };
  return (
    <>
      <Box
        onClick={handleMenu}
        sx={{
          '&:hover': {
            backgroundColor: colors.background.paper,
          },
          alignItems: 'center',
          backgroundColor: colors.background.paper,
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          gap: 1,
          height: '48px',
          ml: 1,
          padding: '6px 12px',
          transition: 'background 0.2s',
        }}
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar
          sx={{
            bgcolor: colors.primary,
            color: colors.background.paper,
            height: 32,
            width: 32,
          }}
        >
          {username?.[0] || 'U'}
        </Avatar>
        <Typography
          sx={{ color: colors.primary, fontSize: '0.95rem', fontWeight: 500 }}
        >
          {username}
        </Typography>
        <ExpandMoreIcon sx={{ color: colors.primary, fontSize: 20 }} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            minWidth: 180,
            mt: 1.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleGetHelp}>
          <ListItemIcon>
            <HelpOutlineIcon fontSize="small" />
          </ListItemIcon>
          Get Help
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSignOut} disabled={loading}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {loading ? 'Signing out...' : 'Sign out'}
        </MenuItem>
      </Menu>
      <HelpModal
        isOpen={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />
    </>
  );
}
